import { Button } from '@/components/ui';
import { ArrowRight, CalendarCheck, Gem } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { getUserData } from '@/utils/cookies';
import { useEffect, useState } from 'react';

const PLUMAA_LOGO_URL =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';

type UserSiteTutorialWelcomeProps = {
	/** Avança para a fase seguinte do tutorial (spotlight da sidebar). */
	onComplete: () => void;
};

/**
 * Primeira fase do tutorial pós-formulários (antes do passo da sidebar).
 * `plumaa_user_site_tutorial_pending` === `welcome`.
 */
const UserSiteTutorialWelcome = ({
	onComplete,
}: UserSiteTutorialWelcomeProps) => {
	const user = getUserData();
	const displayName = user?.first_name?.trim() || 'Você';

	const [screenEntered, setScreenEntered] = useState(false);
	const [showPrimaryButton, setShowPrimaryButton] = useState(false);

	useEffect(() => {
		setScreenEntered(false);
		setShowPrimaryButton(false);

		let innerRaf = 0;
		const outerRaf = window.requestAnimationFrame(() => {
			innerRaf = window.requestAnimationFrame(() =>
				setScreenEntered(true),
			);
		});

		const buttonId = window.setTimeout(
			() => setShowPrimaryButton(true),
			1800,
		);

		return () => {
			window.cancelAnimationFrame(outerRaf);
			if (innerRaf) window.cancelAnimationFrame(innerRaf);
			window.clearTimeout(buttonId);
		};
	}, []);

	const handleContinue = () => {
		onComplete();
	};

	const featureCards = [
		{
			icon: CalendarCheck,
			title: 'Sessões com profissionais',
			text: 'Agende e realize sessões com psicólogos e médicos direto pela Plumaa, com o cuidado organizado em um só lugar.',
		},
		{
			icon: Gem,
			title: 'Clubes com benefícios',
			text: 'Participe de clubes com vantagens pensadas para usar dentro da plataforma e complementar sua jornada.',
		},
	] as const;

	return (
		<section className="relative flex min-h-full w-full items-start justify-center px-3 py-4 md:items-center md:px-5 md:py-6">
			<Helmet>
				<title>Bem-vindo à Plumaa | Tour</title>
			</Helmet>

			<div
				className={`relative z-0 mx-auto min-h-full w-full max-w-[1120px] transition-all duration-700 ease-out ${
					screenEntered
						? 'translate-y-0 opacity-100'
						: 'translate-y-6 opacity-0'
				}`}
			>
				<div
					className={`rounded-[2.25rem] border border-zinc-200 bg-white p-6 shadow-sm transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:p-10 ${
						screenEntered ? 'scale-100' : 'scale-[0.97]'
					}`}
				>
					<div
						className={`flex justify-center transition-opacity duration-500 ease-out md:pt-2 ${
							screenEntered ? 'opacity-100' : 'opacity-0'
						}`}
						style={{ transitionDelay: '80ms' }}
					>
						<img
							src={PLUMAA_LOGO_URL}
							alt="Plumaa"
							className="h-14 w-auto object-contain md:h-16"
							draggable={false}
						/>
					</div>

					<section
						className={`mx-auto mt-10 w-full max-w-4xl transition-all duration-500 ease-out md:mt-12 ${
							screenEntered
								? 'translate-y-0 opacity-100'
								: 'translate-y-4 opacity-0'
						}`}
						style={{ transitionDelay: '140ms' }}
					>
						<p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-orange/80">
							Sua jornada continua
						</p>
						<h1 className="mt-3 text-3xl font-black leading-[1.05] tracking-tight text-zinc-900 md:text-5xl">
							{displayName},{' '}
							<span className="text-brand-orange">
								bem-vindo à Plumaa
							</span>
						</h1>
						<div className="mb-4 mt-3 h-1 w-14 rounded-full bg-brand-orange" />
						<p className="text-base leading-relaxed text-zinc-600 md:text-lg">
							Agora você pode explorar a plataforma com calma.
							Aqui você encontra agendamento de sessões com
							profissionais como psicólogos e médicos, clubes com
							benefícios — tudo integrado para apoiar seu cuidado.
						</p>

						<div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
							{featureCards.map(
								({ icon: Icon, title, text }, index) => (
									<div
										key={title}
										className={`flex items-start gap-3 rounded-2xl border border-zinc-100  p-5 ${
											index === 2 ? 'md:col-span-2' : ''
										}`}
									>
										<span className="inline-flex shrink-0 rounded-xl bg-white p-2.5 text-brand-orange shadow-sm ring-1 ring-zinc-100">
											<Icon
												className="h-5 w-5"
												strokeWidth={2}
											/>
										</span>
										<div>
											<p className="text-sm font-bold text-zinc-900">
												{title}
											</p>
											<p className="mt-1.5 text-xs leading-relaxed text-zinc-600">
												{text}
											</p>
										</div>
									</div>
								),
							)}
						</div>

						<p className="mt-8 text-center text-[11px] font-medium text-zinc-400 md:text-xs">
							Ambiente pensado para privacidade e segurança das
							suas informações.
						</p>

						<div className="mt-6 min-h-[52px] md:min-h-[56px]">
							<div
								className={`transition-all duration-500 ease-out ${
									showPrimaryButton
										? 'pointer-events-auto translate-y-0 opacity-100'
										: 'pointer-events-none translate-y-2 opacity-0'
								}`}
							>
								<Button
									type="button"
									text="Explorar a plataforma"
									onClick={handleContinue}
									endIcon={
										<ArrowRight className="size-4 stroke-[2.5] text-white" />
									}
									className="mx-auto flex w-full justify-center rounded-xl bg-brand-orange py-2.5 text-sm font-bold text-white hover:bg-brand-orange/90 md:max-w-md md:py-3 md:text-base"
								/>
							</div>
						</div>
					</section>
				</div>
			</div>
		</section>
	);
};

export { UserSiteTutorialWelcome };
